import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Container, Box, TextField, Button, Typography, MenuItem } from "@mui/material";
import siteLogo from "../assets/rpo_logo.png";
import { fetchRegisterAccess } from "../utils/registerAccess";

/**
 * ✅ CRA API base
 * Dev:  REACT_APP_API_BASE=http://localhost:5000/api
 * Prod: REACT_APP_API_BASE=/rpo/api
 */
const API_BASE = process.env.REACT_APP_API_BASE;

const ROLE_OPTIONS = [
  { value: "recruiter", label: "Recruiter" },
  { value: "candidate", label: "Candidate" },
];

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "candidate",
  });

  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [registerAccessChecked, setRegisterAccessChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    fetchRegisterAccess()
      .then((allowed) => {
        if (!active) return;
        setCanRegister(allowed);
        setRegisterAccessChecked(true);
        if (!allowed) {
          setError("Registration is not available from this network.");
        }
      })
      .catch(() => {
        if (!active) return;
        setCanRegister(false);
        setRegisterAccessChecked(true);
        setError("Unable to verify registration access right now.");
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canRegister) {
      setError("Registration is not available from this network.");
      return;
    }

    const name = String(form.name || "").trim();
    const email = String(form.email || "").trim().toLowerCase();
    const password = String(form.password || "");
    const role = String(form.role || "").trim().toLowerCase();

    if (!name || !email || !password || !role) {
      setError("All fields are required.");
      return;
    }
    if (name.length > 50) {
      setError("Name is too long (max 50 characters).");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Invalid email format.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!["recruiter", "candidate"].includes(role)) {
      setError("Invalid role. Choose Recruiter or Candidate.");
      return;
    }

    setSubmitting(true);

    try {
      // ✅ ROOT auth endpoint (not /api)
      const check = await axios.post(`${API_BASE}/check-email`, { email });
      if (check.data?.exists) {
        setError("User with this email already exists");
        setSubmitting(false);
        return;
      }

      const data = new FormData();
      data.append("name", name);
      data.append("email", email);
      data.append("password", password);
      data.append("role", role);

      if (profileImage) data.append("profile_image", profileImage);

      // ✅ ROOT auth endpoint (not /api)
      const res = await axios.post(`${API_BASE}/register`, data);

      if (!res.data?.success) {
        setError(res.data?.error || "Registration failed");
        setSubmitting(false);
        return;
      }

      // ✅ ROOT auth endpoint (not /api)
      const loginRes = await axios.post(`${API_BASE}/login`, { email, password }, { withCredentials: true });

      if (loginRes.data?.success && loginRes.data?.token) {
        let token = String(loginRes.data.token).trim();
        if (token.toLowerCase().startsWith("bearer ")) token = token.slice(7).trim();

        localStorage.setItem("token", token);

        if (loginRes.data.user) {
          localStorage.setItem("user", JSON.stringify(loginRes.data.user));
        }

        alert("Registered and logged in successfully!");
        navigate("/overview");
      } else {
        setError(loginRes.data?.error || "Login failed after registration");
      }
    } catch (err) {
      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed";

      console.error("Register failed:", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });

      setError(backendMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component={canRegister ? "form" : "div"}
        method={canRegister ? "post" : undefined}
        className="registration-form"
        onSubmit={canRegister ? handleSubmit : undefined}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: 400,
          p: 3,
          borderRadius: 2,
        }}
      >
        <img src={siteLogo} alt="Logo" style={{ width: 120, margin: "0 auto" }} />

        <h1 className="text-white">Disrupting the Recruiting Tech World!</h1>

        {registerAccessChecked && canRegister && (
          <>
            <TextField
              variant="outlined"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              sx={{
                input: { color: "white" },
                fieldset: { borderColor: "white", borderRadius: "30px" },
              }}
            />

            <TextField
              variant="outlined"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              sx={{
                input: { color: "white" },
                fieldset: {
                  borderColor: "white",
                  borderRadius: "30px",
                  backgroundColor: "transparent",
                },
              }}
            />

            <TextField
              variant="outlined"
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              sx={{
                input: { color: "white" },
                fieldset: {
                  borderColor: "white",
                  borderRadius: "30px",
                  backgroundColor: "transparent",
                },
              }}
            />

            <TextField
              select
              variant="outlined"
              label="User Role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              sx={{
                "& .MuiInputLabel-root": { color: "white" },
                "& .MuiSelect-select": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
                fieldset: {
                  borderColor: "white",
                  borderRadius: "30px",
                  backgroundColor: "transparent",
                },
              }}
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              component="label"
              disabled={submitting}
              sx={{
                color: "white",
                border: "2px solid white",
                borderRadius: "30px",
                py: 1.5,
                textTransform: "none",
              }}
            >
              {profileImage ? profileImage.name : "Upload Profile Image"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setProfileImage(e.target.files?.[0] || null)}
              />
            </Button>
          </>
        )}

        {!registerAccessChecked && (
          <Typography sx={{ color: "white", fontSize: 14, textAlign: "center" }}>
            Checking registration access...
          </Typography>
        )}

        {error && (
          <Typography sx={{ color: "red", fontSize: 14, textAlign: "center" }}>
            {error}
          </Typography>
        )}

        {registerAccessChecked && canRegister && (
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              borderRadius: "999px",
              py: 1.5,
              textTransform: "none",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Creating..." : "Register"}
          </Button>
        )}

        <Typography sx={{ color: "white", mt: 1, textAlign: "center" }}>
          Already have an account?{" "}
          <Link to="/" style={{ color: "white", textDecoration: "underline" }}>
            Login
          </Link>
        </Typography>
      </Box>
    </Container>
  );
}
